const MESSAGE_KEY_PATTERN = /^[A-Z][A-Z0-9_]*$/;

const DEFAULT_FALLBACK =
  'Something went wrong. Please try again or contact support if the problem continues.';

export function isExposedTranslationKey(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith('message.') && !trimmed.startsWith('API.')) {
    return MESSAGE_KEY_PATTERN.test(trimmed);
  }
  if (trimmed.startsWith('message.')) {
    const inner = trimmed.slice('message.'.length).trim();
    return !inner || MESSAGE_KEY_PATTERN.test(inner) || true;
  }
  const inner = trimmed.slice('API.'.length).trim();
  return MESSAGE_KEY_PATTERN.test(inner);
}

export function normalizeApiMessageKey(raw: string): string | null {
  let key = raw.trim();
  if (key.startsWith('message.')) {
    key = key.slice('message.'.length).trim();
  }
  if (key.startsWith('API.')) {
    key = key.slice('API.'.length).trim();
  }
  if (MESSAGE_KEY_PATTERN.test(key)) return key;
  return null;
}

export function humanizeMessageKey(key: string): string {
  const cleaned = key.replace(/_/g, ' ').trim().toLowerCase();
  if (!cleaned) return DEFAULT_FALLBACK;
  return `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1)}.`;
}

export type IntlLike = {
  formatMessage: (
    descriptor: { id: string; defaultMessage?: string },
    values?: Record<string, string | number>
  ) => string;
  locale?: string;
};

export function resolveApiMessage(
  raw: string | null | undefined,
  intl: IntlLike,
  fallback: string = DEFAULT_FALLBACK
): string {
  if (!raw || typeof raw !== 'string') return fallback;

  const trimmed = raw.trim();
  if (!trimmed) return fallback;

  const messageKey = normalizeApiMessageKey(trimmed);
  if (messageKey) {
    const apiId = `API.${messageKey}`;
    const translated = intl.formatMessage({
      id: apiId,
      defaultMessage: humanizeMessageKey(messageKey),
    });
    if (translated && translated !== apiId) return translated;
    return humanizeMessageKey(messageKey);
  }

  if (trimmed.startsWith('message.')) {
    const inner = trimmed.slice('message.'.length).trim();
    if (inner) return inner;
  }

  if (isExposedTranslationKey(trimmed)) return fallback;

  return trimmed;
}

export function extractApiMessageFromError(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' ? error : undefined;
  }

  const maybeError = error as {
    data?: unknown;
    message?: unknown;
    error?: unknown;
  };

  if (maybeError.data) {
    if (typeof maybeError.data === 'string') return maybeError.data;
    if (typeof maybeError.data === 'object') {
      const data = maybeError.data as { message?: unknown; error?: unknown };
      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  if (typeof maybeError.message === 'string') return maybeError.message;
  if (typeof maybeError.error === 'string') return maybeError.error;

  return undefined;
}

export function resolveApiMessageFromError(
  error: unknown,
  intl: IntlLike,
  fallback?: string
): string {
  return resolveApiMessage(
    extractApiMessageFromError(error),
    intl,
    fallback
  );
}
